import { mapJSONToCompleteness } from 'legacy/lut/services/partner360/completeness/mappings/mapJSONToCompleteness'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import {
  ATTENTION_STATUS_BITS,
  isAttentionStatusBitRaised,
  parseAttentionStatusMasks,
} from 'partner360/entities/attentionStatuses'

import { getManagerOfferingsInfo, getManagerOverallCounts } from '../../lib'
import type { IManagerOfferingCounts } from '../../lib'
import { isCockpitPlanningStarted } from '../../lib/getCockpitPlanningStarted'
import { getOfferingPlanningTone } from '../../lib/getOfferingPlanningTone'
import {
  selectCockpitCombinedPlanQualityByOffering,
  selectCockpitCompletenessData,
  selectCockpitPlanningStarted,
  selectCockpitRtm,
  selectVisibleCockpitAttentionCountByOffering,
  selectVisibleCockpitAttentionSeverityByOffering,
  selectVisibleCockpitOfferingsCatalog,
  selectVisibleCockpitPartnerData,
} from '../selectors'

import type { ICockpitPlanningStatus, ICockpitPlanningStatuses } from './useCockpitPlanningStatuses'

type TOverallCounts = { complete: number; acknowledged: number; total: number }

const getPlanOverallCounts = (
  offeringCounts: IManagerOfferingCounts[],
  completePartnerIdsByOffering: ReadonlyMap<string, ReadonlySet<string>>
): TOverallCounts => ({
  ...getManagerOverallCounts(offeringCounts),
  complete: offeringCounts.reduce(
    (sum, { offering }) => sum + (completePartnerIdsByOffering.get(offering)?.size ?? 0),
    0
  ),
})

const getPartnerOverallCounts = (
  offeringCounts: IManagerOfferingCounts[],
  completePartnerIdsByOffering: ReadonlyMap<string, ReadonlySet<string>>
): TOverallCounts => {
  const partnerIds = new Set(offeringCounts.flatMap(({ partnerIds: ids }) => ids))
  const isEveryEnabledOffering = (
    partnerId: string,
    isDone: (counts: IManagerOfferingCounts) => boolean
  ) => offeringCounts.every((counts) => !counts.partnerIds.includes(partnerId) || isDone(counts))
  let complete = 0
  let acknowledged = 0

  partnerIds.forEach((partnerId) => {
    if (
      isEveryEnabledOffering(partnerId, ({ offering }) =>
        Boolean(completePartnerIdsByOffering.get(offering)?.has(partnerId))
      )
    ) {
      complete += 1
    }

    if (
      isEveryEnabledOffering(partnerId, ({ acknowledgedPartnerIds }) =>
        acknowledgedPartnerIds.includes(partnerId)
      )
    ) {
      acknowledged += 1
    }
  })

  return { complete, acknowledged, total: partnerIds.size }
}

/**
 * The manager cockpit's counterpart of `useCockpitPlanningStatuses`, over the partners it shows:
 * per programme, how many of the partners that have it enabled have a complete plan and how many
 * have signed it; `Overall` sums both across the programmes. The tone is the most severe attention
 * status any of the partners raised.
 *
 * A plan is complete when its attention mask does not raise `planned` — the same bit the tile's
 * `X plans are not completed` counts, so the two never disagree. The cockpit's payload carries no
 * `Completeness`, so the partner's own profile is read only for a plan with no mask.
 *
 * A distributor completes and signs all its programmes as one JBP plan, so on the distributors'
 * view `Overall` counts partners, not plans: a partner is complete or signed when every programme
 * it has enabled is.
 */
export const useManagerCockpitPlanningStatuses = (): ICockpitPlanningStatuses => {
  const offeringsCatalog = useSelector(selectVisibleCockpitOfferingsCatalog)
  const partnerData = useSelector(selectVisibleCockpitPartnerData)
  const completenessData = useSelector(selectCockpitCompletenessData)
  const planQuality = useSelector(selectCockpitCombinedPlanQualityByOffering)
  const attentionSeverities = useSelector(selectVisibleCockpitAttentionSeverityByOffering)
  const attentionCounts = useSelector(selectVisibleCockpitAttentionCountByOffering)
  const planningStarted = useSelector(selectCockpitPlanningStarted)
  const selectedRtm = useSelector(selectCockpitRtm)

  return useMemo(() => {
    const completenessByPartner = new Map(
      partnerData.map(({ PartnerId, Completeness }) => [
        PartnerId,
        mapJSONToCompleteness(Completeness ?? '[]'),
      ])
    )
    const masksByPartner = new Map(
      partnerData.map(({ PartnerId, CockpitAttentionStatus }) => [
        PartnerId,
        parseAttentionStatusMasks(CockpitAttentionStatus),
      ])
    )
    const isPlanComplete = (partnerId: string, offering: string) => {
      const planMask = masksByPartner.get(partnerId)?.find((entry) => entry.offering === offering)

      if (planMask) {
        return !isAttentionStatusBitRaised(planMask.mask, ATTENTION_STATUS_BITS.planned)
      }

      /* Only a whole plan counts, as on the single-partner line: 99.8% is not finished. */
      return Boolean(
        completenessByPartner
          .get(partnerId)
          ?.some(({ profile, total: share }) => profile === offering && share >= 1)
      )
    }
    const byOffering = new Map<string, ICockpitPlanningStatus>()
    const offeringCounts = getManagerOfferingsInfo(offeringsCatalog, completenessData)
    const completePartnerIdsByOffering = new Map<string, Set<string>>()

    offeringCounts.forEach(({ offering, acknowledged, total, partnerIds }) => {
      const completePartnerIds = new Set(
        partnerIds.filter((partnerId) => isPlanComplete(partnerId, offering))
      )
      const complete = completePartnerIds.size

      completePartnerIdsByOffering.set(offering, completePartnerIds)

      byOffering.set(offering, {
        completeness: { count: complete, total },
        acknowledgment: { count: acknowledged, total },
        score: planQuality.byOffering.get(offering) ?? null,
        tone: getOfferingPlanningTone(
          attentionSeverities.get(offering),
          isCockpitPlanningStarted(planningStarted, offering)
        ),
        attentionCount: attentionCounts.byOffering.get(offering) ?? 0,
      })
    })

    const overallCounts =
      selectedRtm === 'distributors'
        ? getPartnerOverallCounts(offeringCounts, completePartnerIdsByOffering)
        : getPlanOverallCounts(offeringCounts, completePartnerIdsByOffering)

    return {
      overall: {
        completeness: { count: overallCounts.complete, total: overallCounts.total },
        acknowledgment: { count: overallCounts.acknowledged, total: overallCounts.total },
        score: planQuality.overallScore,
        tone: getOfferingPlanningTone(
          attentionSeverities.size ? Math.max(...attentionSeverities.values()) : undefined,
          planningStarted.overall
        ),
        attentionCount: attentionCounts.overall,
      },
      byOffering,
      severities: attentionSeverities,
    }
  }, [
    attentionCounts,
    attentionSeverities,
    completenessData,
    offeringsCatalog,
    partnerData,
    planQuality,
    planningStarted,
    selectedRtm,
  ])
}
